import {
  Accordion,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { ExpandMore } from '@mui/icons-material';
import ExceptionTables, { isErroreDocumento } from 'practice/components/shared/exception-tables';
import { EccezioneVOList } from 'shell/models/practice';
import { transformerExeptionDetail } from 'practice/utils/practice-detail-transform';
import { useFormik } from 'formik';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { PmiButton } from 'shell/components/shared/button';
import ModalUnavailability from './reason-unavailibility.dialog';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import { useNavigate, useParams } from 'react-router-dom';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import ModalPrintSuccessUnavailibility from './print-success-unavailibility-dialog';
import { Attachment } from 'shell/models/attachment';
import { PracticeDraftStep } from 'practice/constants/practice-management-step';
import { noop } from 'shell/utils/noop';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { PracticeSearchType } from 'practice/pages/storage-practice';

interface AccordionExceptionListProps {
  eccezioni: EccezioneVOList;
}

const AccordionExceptionListComponent = ({
  eccezioni,
}: AccordionExceptionListProps): React.ReactElement => {
  const [open, setOpen] = useState(true);
  const params = useParams();

  const filterTable = useFilterTable<PracticeSearchType>();

  const pdf = useRef<string | null>();

  const [openModalIrricevibilie, setOpenModalIrricevibile] = useState<boolean>();
  const [openModalPrintSuccessUnavailibility, setOpenModalPrintSuccessUnavailibility] =
    useState<boolean>(false);

  const [pdfModal, setPdfModal] = useState({ data: '', open: false, name: '' });

  const navigate = useNavigate();

  const printUnavailability = useHttpClient<Blob>(
    `${__PRACTICE_API_URL__}/pratiche/stampa/documento-irricevibilita`,
    {
      options: { method: 'POST' },
    }
  );
  const setFlagEccezioniIgnorabili = useHttpClient(
    `${__PRACTICE_API_URL__}/pratiche/eccezioni/eccezioni-ignorabili`,
    {
      options: {
        method: 'PUT',
      },
    }
  );

  const insertData = useHttpClient(`${__PRACTICE_API_URL__}/pratiche/allegati`, {
    options: { method: 'POST' },
  });

  const codiceEnte = useMemo(() => {
    return params.codiceEnte;
  }, [params.codiceEnte]);

  const tipoRegistro = useMemo(() => {
    return params.tipoRegistro;
  }, [params.tipoRegistro]);

  const idAnnoPratica = useMemo(() => {
    return params.idAnnoPratica;
  }, [params.idAnnoPratica]);

  const progressivoPratica = useMemo(() => {
    return params.progressivoPratica;
  }, [params.progressivoPratica]);

  const saveUnavailabilityFile = useCallback(() => {
    const data: Attachment = {
      codiceEnte: codiceEnte || undefined,
      tipoRegistro: tipoRegistro || undefined,
      idAnnoPratica: idAnnoPratica ? Number.parseInt(idAnnoPratica, 10) : undefined,
      progressivoPratica: progressivoPratica ? Number.parseInt(progressivoPratica, 10) : undefined,
      nomeFile: 'Pratica Irricevibile.pdf',
      idTipoFormato: 1,
      idTipoAllegato: 11,
      contenutoAllegato: pdfModal.data,
      tipoOrigine: 'C',
      stepBozza: PracticeDraftStep.Irricevibilita,
    };

    (insertData({ data }) as ResponsePromise<void>)
      .then(() => {
        toast.success('Operazione Eseguita con successo');
        setPdfModal({ data: '', open: false, name: '' });
        setOpenModalPrintSuccessUnavailibility(false);
        filterTable.setValues({
          ...filterTable.values,
          fromNumber: params.progressivoPratica ? Number(params.progressivoPratica) : null,
          atNumber: params.progressivoPratica ? Number(params.progressivoPratica) : null,
        });
        navigate('/pratiche/ricerca');
      })
      .catch(noop);
  }, [codiceEnte, tipoRegistro, idAnnoPratica, progressivoPratica, pdfModal.data]);

  const eccezioniBloccanti = useMemo(() => {
    return transformerExeptionDetail(eccezioni?.data?.payload?.eccezioni);
  }, [eccezioni?.data?.payload?.eccezioni]);

  const eccezioniIgnorabili = useMemo(() => {
    return transformerExeptionDetail(eccezioni?.data?.payload?.eccezioniIgnorabili);
  }, [eccezioni?.data?.payload?.eccezioniIgnorabili]);

  // il flag "ignora eccezioni catasto" non si applica agli errori documenti
  const hasMessaggiCatasto = useMemo(
    () => (eccezioniIgnorabili || []).some((item) => !isErroreDocumento(item)),
    [eccezioniIgnorabili]
  );

  const formik = useFormik<{ ignoreException?: boolean }>({
    initialValues: {
      ignoreException: eccezioni?.data?.payload?.flagIgnoraErrore === '1',
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      (
        setFlagEccezioniIgnorabili({
          data: {
            eccezioni: eccezioni?.data?.payload?.eccezioni,
            flagIgnoraErrore: values.ignoreException ? '1' : undefined,
            eccezioniIgnorabili: eccezioni?.data?.payload?.eccezioniIgnorabili,
          },
        }) as ResponsePromise<void>
      ).then(() => {
        toast.success('Operazione Eseguita con successo');
      });
    },
  });

  return (
    <Accordion
      expanded={open}
      onChange={() => {
        setOpen(!open);
      }}
    >
      <AccordionSummary
        aria-controls="accordion-exception-list-content"
        id="accordion-exceptino-list-header"
        expandIcon={<ExpandMore />}
      >
        <Typography>Elenco eccezioni</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={1}>
          {hasMessaggiCatasto ? (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    id="ignoreException"
                    checked={formik.values?.ignoreException || false}
                    onChange={() =>
                      formik.setFieldValue('ignoreException', !formik.values?.ignoreException)
                    }
                    inputProps={{
                      'aria-label': 'ignoreException',
                    }}
                  />
                }
                label="Ignora eccezioni catasto"
              />
            </Grid>
          ) : undefined}
          <Grid item xs={12}>
            <ExceptionTables
              eccezioniIgnorabili={eccezioniIgnorabili}
              eccezioniAnpr={eccezioniBloccanti}
            />
          </Grid>
          <ModalPrintSuccessUnavailibility
            onNo={() => {
              setOpenModalPrintSuccessUnavailibility(false);
              setPdfModal({
                data: pdf.current || '',
                name: 'Pratica Irricevibile',
                open: true,
              });
            }}
            onYes={() => {
              saveUnavailabilityFile();
            }}
            open={openModalPrintSuccessUnavailibility}
            onClose={() => {
              setOpenModalPrintSuccessUnavailibility(true);
            }}
          />
          <ModalUnavailability
            open={openModalIrricevibilie || false}
            onClose={() => {
              setOpenModalIrricevibile(false);
            }}
            onConfirm={() => {
              setOpenModalIrricevibile(false);
              (
                printUnavailability({
                  data: {
                    codiceEnte: codiceEnte,
                    tipoRegistro: tipoRegistro,
                    idAnnoPratica: idAnnoPratica,
                    progressivoPratica: progressivoPratica,
                  },
                  responseType: 'blob',
                }) as ResponsePromise<Blob>
              ).then((response) => {
                const blob = new Blob([response?.data || ''], { type: 'application/pdf' });
                blobToBase64(blob).then((base64) => {
                  const [, pdfBase64] = base64.split(',');
                  pdf.current = pdfBase64;
                  setPdfModal({
                    data: base64.split(',')[1],
                    name: 'Pratica Irricevibile',
                    open: true,
                  });
                });
              });
            }}
          />
          <ModalViewerPDF
            open={pdfModal.open}
            data={pdfModal.data}
            name={pdfModal.name}
            title="Pratica Irricevibile"
            onClose={() => {
              setPdfModal({ ...pdfModal, open: false });
              setOpenModalPrintSuccessUnavailibility(true);
            }}
          />
          <Grid container spacing={1} item xs={12} justifyContent="space-between">
            <Grid item xs={12} md={4}>
              <PmiButton
                fullWidth
                variant="outlined"
                color="cdmBrandThird"
                onClick={() => {
                  setOpenModalIrricevibile(true);
                }}
              >
                Pratica Irricevibile
              </PmiButton>
            </Grid>
            <Grid item xs={12} md={4}>
              <PmiButton
                fullWidth
                variant="outlined"
                color="cdmBrandThird"
                onClick={() => {
                  formik.handleSubmit();
                }}
              >
                Conferma
              </PmiButton>
            </Grid>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

AccordionExceptionListComponent.displayName = 'Practice State - Table - accordion practice state';

const AccordionExceptionList = memo(AccordionExceptionListComponent);

export default AccordionExceptionList;
